import { ConsultTransferComponent } from '../consult-transfer/consult-transfer.component';
import { PhoneService } from '../phone.service';

export class ConversationUtils {
  isConversationRecentlyTerminated(conversationId: string, terminatedConversations: Set<string>): boolean {
    return terminatedConversations.has(conversationId);
  }

  trackTerminatedConversation(conversationId: string | undefined, terminatedConversations: Set<string>): void {
    if (!conversationId || terminatedConversations.has(conversationId)) return;

    terminatedConversations.add(conversationId);
    setTimeout(() => terminatedConversations.delete(conversationId), 10000);
  }

  getAgentParticipant(conversation: any, userId: string) {
    return conversation?.participants?.findLast((participant: any) => participant.user?.id === userId && ['agent', 'user'].includes(participant.purpose!));
  }

  getCustomerParticipant(conversation: any) {
    return conversation?.participants?.findLast((participant: any) => ['external','customer'].includes(participant.purpose!));
  }

  isConnected(participant: any): boolean {
    return ['connected'].includes(participant?.state);
  }

  isAlerting(participant: any): boolean {
    return ['alerting', 'dialing', 'contacting', 'offering'].includes(participant?.state);
  }

  isDisconnectedOrTerminated(participant: any): boolean {
    return ['disconnected', 'terminated'].includes(participant?.state);
  }

  formatPhoneNumber(phoneNumber: string, phoneService: PhoneService): string {
    const strippedNumber = phoneNumber.replace(/[^\d]/g, '');
    return phoneService.formatPhoneNumber(strippedNumber.substring(1));
  }

  shouldIgnoreConversation(conversationId: string, terminatedConversations: Set<string>): boolean {
    if (this.isConversationRecentlyTerminated(conversationId, terminatedConversations)) {
      return true;
    }
    return false;
  }

  updateCallStateForAgent(
    agentParticipant: any,
    conversationId: string,
    currentCallConversationId: string | undefined,
    onAgentEvent: (participant: any | undefined) => void,
    onAlerting: () => void,
    onConnected: () => void,
    onDisconnected: () => void
  ): void {
    if (currentCallConversationId !== conversationId && this.isAlerting(agentParticipant)) {
      currentCallConversationId = conversationId;
      onAlerting();
      onAgentEvent(agentParticipant);
    }
    if (currentCallConversationId === conversationId) {
      if (this.isConnected(agentParticipant)) {
        onAgentEvent(agentParticipant);
        onConnected();
      }
      if (this.isDisconnectedOrTerminated(agentParticipant)) {
        onDisconnected();
      }
    }
  }

  getParticipantIdIfConnected(participant: any): string | undefined {
    return this.isConnected(participant) ? participant.id : undefined;
  }

  clearConversationState(
    callConversationId: string | undefined,
    emailConversationId: string | undefined,
    terminatedCallConversations: Set<string>,
    terminatedEmailConversations: Set<string>
  ): void {
    this.trackTerminatedConversation(callConversationId, terminatedCallConversations);
    this.trackTerminatedConversation(emailConversationId, terminatedEmailConversations);
  }

  handleParticipantEvents(
    {conversation,userId,currentCallConversationId,phoneService,onAgentEvent,onCustomerEvent,onAlerting,onConnected,onDisconnected} : {
    conversation: any,
    userId: string,
    currentCallConversationId: string | undefined,
    phoneService: PhoneService,
    onAgentEvent: (participant: any | undefined) => void,
    onCustomerEvent: (phoneNumber: string, participantId: string | undefined, participant: any) => void,
    onAlerting: () => void,
    onConnected: () => void,
    onDisconnected: () => void }
  ): void {
    // Handle agent participant
    const agentParticipantEntity = this.getAgentParticipant(conversation, userId);
    if (agentParticipantEntity) {
      this.updateCallStateForAgent(agentParticipantEntity, conversation.id, currentCallConversationId,onAgentEvent,onAlerting, onConnected, onDisconnected);
    }

    // Handle customer participant
    const customerParticipant = this.getCustomerParticipant(conversation);
    if (customerParticipant) {
      const phoneNumber = this.formatPhoneNumber(customerParticipant.address, phoneService);
      const participantId = this.getParticipantIdIfConnected(customerParticipant);
      onCustomerEvent(phoneNumber, participantId, customerParticipant);
    }
  }

  handleManualDial({
    phoneNumber,
    phoneService,
    onValidNumber
  }: {
    phoneNumber: string;
    phoneService: PhoneService;
    onValidNumber: () => void;
  }): void {
    if (!phoneService.isValidNumber(phoneNumber)) return;
    onValidNumber();
  }

  toggleCall({
    conversationId,
    queueId,
    participantId,
    phoneNumber,
    callState,
    phoneService,
    localTouch,
    updateCallState,
    updateConversationId,
    clearState,
    showError
  }: {
    conversationId : string | undefined;
    queueId: string | undefined;
    participantId: string | undefined;
    phoneNumber: string;
    callState: string;
    phoneService: PhoneService;
    localTouch: boolean;
    updateCallState: (newState: string) => void;
    updateConversationId: (conversationId: string | undefined) => void;
    clearState: () => void;
    showError: (errorMessage: string) => void;
  }): void {
    if (!phoneService.isValidNumber(phoneNumber)) return;

    if (callState === 'none' || callState === 'loading') {
      updateCallState('dialing');
      phoneService.makeCall(phoneNumber, localTouch).then(conversationId => {
        if (!conversationId) {
          updateCallState('none');
        } else {
          updateConversationId(conversationId);
        }
      });
    } else if (['connected','alerting', 'dialing', 'contacting', 'offering'].includes(callState)) {
      
      if(callState == 'connected') {
        phoneService.disconnect(conversationId!).then(() => {
          clearState();
        });
        return;
      }

      if(queueId) {
        phoneService.decline(conversationId!,participantId!).then(() => {
          clearState();
        });
      }
      else {
        phoneService.voiceMail(conversationId!, participantId!).then(() => {
          clearState();
        });
      }
    } else {
      showError('Invalid call Disconnect');
    }
  }

  handleConsultTransfer({
    dialog,
    callConversationId,
    customerParticipantId,
    agentParticipantId,
    onDialogClosed
  }: {
    dialog: any;
    callConversationId: string | undefined;
    customerParticipantId: string | undefined;
    agentParticipantId: string | undefined;
    onDialogClosed: (result: any) => void;
  }): void {
    const dialogRef = dialog.open(ConsultTransferComponent, {
      disableClose: true,
      data: {
        conversationId: callConversationId,
        customerParticipantId: customerParticipantId,
        agentParticipantId: agentParticipantId
      }
    });

    dialogRef.afterClosed().subscribe(onDialogClosed);
  }
}
